/* =========================================================
   collection-tabs-slider.js
   Tab switching + horizontal slider + progress bar.
   No dependencies.
   ========================================================= */

class PBCollectionTabsSlider {
  constructor(el) {
    this.el = el;
    this.tabs = Array.from(el.querySelectorAll('[data-tabs] .pb-coll-tabs__tab'));
    this.panels = Array.from(el.querySelectorAll('.pb-coll-tabs__panel'));
    this.progressEl = el.querySelector('[data-progress]');
    this.prevBtn = el.querySelector('[data-arrow-prev]');
    this.nextBtn = el.querySelector('[data-arrow-next]');

    this.activeIndex = 0;
    this.activePanel = this.panels[0] || null;
    this.activeTrack = null;

    this.bindTabs();
    this.bindArrows();
    this.setActiveTrack(this.activePanel);
    this.bindShopifyEditor();
  }

  /* ---------- Tab logic ---------- */
  bindTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(Number(tab.dataset.tabIndex)));
      tab.addEventListener('keydown', (e) => this.onTabKey(e));
    });
  }

  onTabKey(e) {
    const horizontal = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!horizontal.includes(e.key)) return;
    e.preventDefault();
    const last = this.tabs.length - 1;
    let next = this.activeIndex;
    if (e.key === 'ArrowRight') next = this.activeIndex === last ? 0 : this.activeIndex + 1;
    else if (e.key === 'ArrowLeft') next = this.activeIndex === 0 ? last : this.activeIndex - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    this.selectTab(next);
    this.tabs[next].focus();
  }

  selectTab(index) {
    if (index === this.activeIndex || !this.tabs[index]) return;
    this.tabs.forEach((t, i) => {
      const active = i === index;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', active ? 'true' : 'false');
      t.setAttribute('tabindex', active ? '0' : '-1');
    });
    this.panels.forEach((p, i) => {
      const active = i === index;
      p.classList.toggle('is-active', active);
      if (active) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
    this.activeIndex = index;
    this.activePanel = this.panels[index];
    this.setActiveTrack(this.activePanel);
  }

  /* ---------- Slider (per-panel) ---------- */
  setActiveTrack(panel) {
    if (this.activeTrack && this.onScroll) {
      this.activeTrack.removeEventListener('scroll', this.onScroll);
    }
    if (!panel) {
      this.activeTrack = null;
      this.updateProgress(0);
      this.updateArrowStates(0, 0);
      return;
    }
    this.activeTrack = panel.querySelector('[data-track]');
    if (!this.activeTrack) return;

    // PB:MODIFIED 2026-06-12 — coalesce scroll handling to one run per frame.
    // syncFromScroll reads scrollWidth/clientWidth (layout-read props); firing
    // it on every raw scroll event forced repeated style/layout recalcs and was
    // a "Minimize main-thread work" (Style & Layout) contributor. rAF-throttling
    // is visually identical — the progress bar/arrows still update each frame.
    this._scrollTicking = false;
    this.onScroll = () => {
      if (this._scrollTicking) return;
      this._scrollTicking = true;
      requestAnimationFrame(() => {
        this._scrollTicking = false;
        this.syncFromScroll();
      });
    };
    this.activeTrack.addEventListener('scroll', this.onScroll, { passive: true });
    // Defer so layout is committed before measuring
    requestAnimationFrame(() => this.syncFromScroll());
  }

  syncFromScroll() {
    if (!this.activeTrack) return;
    const total = this.activeTrack.scrollWidth;
    const visible = this.activeTrack.clientWidth;
    const pos = this.activeTrack.scrollLeft;
    const max = total - visible;
    const fraction = total > 0 ? (pos + visible) / total : 1;
    this.updateProgress(fraction);
    this.updateArrowStates(pos, max);
  }

  updateProgress(fraction) {
    if (!this.progressEl) return;
    this.progressEl.style.transform = `scaleX(${Math.max(0, Math.min(1, fraction))})`;
  }

  updateArrowStates(pos, max) {
    const hasOverflow = max > 0;
    if (this.prevBtn) this.prevBtn.disabled = !hasOverflow || pos <= 1;
    if (this.nextBtn) this.nextBtn.disabled = !hasOverflow || pos >= max - 1;
  }

  bindArrows() {
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollBySlide(1));
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollBySlide(-1));
  }

  scrollBySlide(direction) {
    if (!this.activeTrack) return;
    const card = this.activeTrack.querySelector('.pb-coll-tabs__card-wrap');
    const gap = parseFloat(getComputedStyle(this.activeTrack).columnGap || getComputedStyle(this.activeTrack).gap || '16');
    const step = card ? card.getBoundingClientRect().width + gap : this.activeTrack.clientWidth * 0.8;
    this.activeTrack.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  /* ---------- Shopify theme editor ---------- */
  bindShopifyEditor() {
    this.onBlockSelect = (e) => {
      if (!this.el.contains(e.target)) return;
      const panel = e.target.closest('.pb-coll-tabs__panel');
      if (!panel) return;
      const idx = this.panels.indexOf(panel);
      if (idx >= 0) this.selectTab(idx);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  destroy() {
    if (this.activeTrack && this.onScroll) this.activeTrack.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }
}

/* ---------- Init ---------- */
function initPBCollTabs(root) {
  const scope = root || document;
  scope.querySelectorAll('.pb-coll-tabs').forEach((el) => {
    if (!el.__pbCollTabs) el.__pbCollTabs = new PBCollectionTabsSlider(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPBCollTabs());
} else {
  initPBCollTabs();
}

document.addEventListener('shopify:section:load', (e) => initPBCollTabs(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('.pb-coll-tabs').forEach((el) => {
    if (el.__pbCollTabs) {
      el.__pbCollTabs.destroy();
      delete el.__pbCollTabs;
    }
  });
});
